import { useAuth0 } from "@auth0/auth0-react";
import { useState, useCallback } from "react";
import { ApiResponse } from "../models/api-response";
import { sendPasswordResetEmail } from "./auth0.service";

export function useAuthenticatedApi() {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  
  const callApi = useCallback(async <T>(
    apiFunction: (data: T, token: string) => Promise<ApiResponse>,
    data: T
  ) => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const result = await apiFunction(data, token);
      return result;
    } catch (error) {
      return { data: null, error: { message: String(error) } };
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);
  
  return { callApi, loading };
}

export function useAuthActions() {
  const { user } = useAuth0();
  const [loading, setLoading] = useState(false);

  const changePassword = useCallback(async () => {
    if (!user?.email) {
      throw new Error("User email not available");
    }

    setLoading(true);
    try {
      // Use Auth0's authentication API directly
      const result = await sendPasswordResetEmail(user.email);
      
      if (result.error) {
        throw new Error(result.error.message);
      }
      
      return result.data;
    } catch (error) {
      console.error("Error requesting password change:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user]);

  return { changePassword, loading };
}